<template>
  <div>
    <transition name="fade">
      <div
        v-if="showUpdateModal"
        style="
          z-index: 9999;
          position: fixed;
          top: 0;
          background-color: rgb(7, 186, 189, 0.75);
          width: 100%;
          height: 50px;
          padding-top: 12.5px;
        "
        class="text-white text-center py-auto"
      >
        Une nouvelle version de {{ isGroupX ? "Group X" : "Monica Groups" }} est
        disponible.
        <b-button
          variant="outline-light"
          class="ml-2"
          size="sm"
          @click="refreshPage()"
        >
          Rafraîchir la page
        </b-button>
      </div>
    </transition>
  
    <div id="app" class="h-100" :class="[skinClasses]">
      <component :is="layout">
        <router-view />
      </component>
    </div>
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { useApplicationContext } from "@/shared/composables/use-application-context";
import { provideToast } from "vue-toastification/composition";
import { ref, watch, onMounted } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import { BButton } from "bootstrap-vue";
import { useWindowSize, useCssVar } from "@vueuse/core";
import dayjs from "dayjs";
import Tenant from "@/plugins/tenant";
import { useOrganizationApi } from "@/modules/authnz/composables/use-organization-api";
import { useUserApi } from "@/modules/users/composables/use-user-api";
import {
  AUTH_STORE_NAMESPACE,
  UPDATE_AUTH_METHOD_ACTION,
  SET_API_URL_ACTION,
  SET_ORG_SETTINGS_ACTION,
  IS_AUTHENTICATED_GETTER,
  SET_ROLES_ACTION,
} from "@/modules/authnz/models/store";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");
const LayoutPublic = () => import("@/layouts/public/LayoutPublic.vue");

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    LayoutPublic,
    BButton,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      if (this.$route.meta.layout === "public") return "layout-public";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
    isGroupX() {
      return Tenant.resolveTenantId().toLocaleLowerCase() === "transat";
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  setup(props, context) {
    const { skin, skinClasses } = useAppConfig();
    const { httpClient, store } = useApplicationContext();
    const { getOrganizationSettings } = useOrganizationApi();
    const { getCurrentUserProfile } = useUserApi();
    const publicAccessToken = context.root.$route.query.token;
    const currentVersion = ref(null);
    const showUpdateModal = ref(false);

    const refreshPage = () => {
      window.location.reload(true);
    };

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    let showBanner = ref(true);

    const retrieveAppConfig = async () => {
      try {
        const response = await httpClient.get("/config");
        if (response.data.api && response.data.api.url) {
          store.dispatch(
            `${AUTH_STORE_NAMESPACE}/${SET_API_URL_ACTION}`,
            response.data.api.url
          );
          httpClient.defaults.baseURL = response.data.api.url;
        }

        if (response.data.authentication.method) {
          store.dispatch(
            `${AUTH_STORE_NAMESPACE}/${UPDATE_AUTH_METHOD_ACTION}`,
            response.data.authentication.method
          );
        }
      } catch (error) {
        console.error(error);
      }
    };
    retrieveAppConfig();

    onMounted(async () => {
      fetch("/version.json", { cache: "no-store" })
        .then((res) => res.json())
        .then((meta) => {
          console.debug("version retrieved:", meta.version);
          currentVersion.value = meta.version;

          setInterval(() => {
            fetch("/version.json", { cache: "no-store" })
              .then((res) => res.json())
              .then((newMeta) => {
                console.debug("version retrieved:", newMeta.version);

                if (newMeta.version !== currentVersion.value) {
                  showUpdateModal.value = true;
                }
              });
          }, 60000);
        });

      const isAuthenticated = await store.getters[
        `${AUTH_STORE_NAMESPACE}/${IS_AUTHENTICATED_GETTER}`
      ];
      if (isAuthenticated) {
        try {
          const settings = await getOrganizationSettings(publicAccessToken);
          store.dispatch(`${AUTH_STORE_NAMESPACE}/${SET_ORG_SETTINGS_ACTION}`, {
            settings,
          });

          const userProfile = await getCurrentUserProfile();
          store.dispatch(
            `${AUTH_STORE_NAMESPACE}/${SET_ROLES_ACTION}`,
            userProfile.roles
          );
        } catch (e) {
          console.error("Error: trying loading org config:", e);
        }
      }
    });

    return {
      skinClasses,
      showBanner,
      showUpdateModal,
      refreshPage,
    };
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Roboto&display=swap");
</style>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
